import type { WebhookLog } from '@data-hub/shared';
import { createFileRoute } from '@tanstack/react-router';
import { AlertTriangleIcon, CheckCircle2Icon, EyeIcon, XCircleIcon } from 'lucide-react';
import { useState } from 'react';
import { AdminGuard } from '@/components/layout/AdminGuard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useWebhookLogs } from '@/features/webhook-logs/use-webhook-logs';

export const Route = createFileRoute('/_authed/admin/webhook-logs')({
  component: WebhookLogsPage,
});

function WebhookLogsPage() {
  return (
    <AdminGuard>
      <WebhookLogsContent />
    </AdminGuard>
  );
}

function WebhookLogsContent() {
  const logs = useWebhookLogs();
  const [detail, setDetail] = useState<WebhookLog | null>(null);
  const items = logs.data?.items ?? [];

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-4 py-8 sm:px-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Webhook logs</h1>
        <p className="text-sm text-muted-foreground">
          Last 100 webhook events received via the public `/webhooks/*` endpoints.
        </p>
      </div>

      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[110px]">Status</TableHead>
              <TableHead>Resource</TableHead>
              <TableHead className="w-[120px]">Source</TableHead>
              <TableHead className="w-[120px]">Token</TableHead>
              <TableHead>Timestamp</TableHead>
              <TableHead className="w-[60px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs.isPending ? (
              Array.from({ length: 6 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={6}>
                    <Skeleton className="h-6 w-full" />
                  </TableCell>
                </TableRow>
              ))
            ) : logs.isError ? (
              <TableRow>
                <TableCell colSpan={6} className="py-12 text-center text-destructive">
                  {logs.error.message}
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="py-12 text-center text-muted-foreground">
                  No webhook events recorded yet.
                </TableCell>
              </TableRow>
            ) : (
              items.map((log) => (
                <TableRow key={log.id}>
                  <TableCell>
                    <StatusBadge status={log.status} />
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-medium">{log.resourceTitle ?? '—'}</span>
                      {log.error && <span className="text-xs text-destructive">{log.error}</span>}
                    </div>
                  </TableCell>
                  <TableCell className="capitalize text-xs">{log.source ?? '—'}</TableCell>
                  <TableCell className="font-mono text-[11px] text-muted-foreground">
                    {log.tokenId ?? '—'}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {formatTimestamp(log.timestamp)}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="View payload"
                      className="size-8"
                      onClick={() => setDetail(log)}
                    >
                      <EyeIcon className="size-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Sheet open={detail !== null} onOpenChange={(open) => !open && setDetail(null)}>
        <SheetContent side="right" className="w-full sm:max-w-xl">
          <SheetHeader>
            <SheetTitle>{detail?.resourceTitle ?? 'Webhook event'}</SheetTitle>
            <SheetDescription>
              {detail
                ? `${detail.source ?? 'unknown source'} · ${formatTimestamp(detail.timestamp)}`
                : ''}
            </SheetDescription>
          </SheetHeader>
          {detail && (
            <div className="flex flex-col gap-4 px-4 pb-6">
              <div className="flex flex-wrap gap-2">
                <StatusBadge status={detail.status} />
                {detail.category && <Badge variant="secondary">{detail.category}</Badge>}
                {detail.tokenId && (
                  <Badge variant="outline" className="font-mono text-[11px]">
                    {detail.tokenId}
                  </Badge>
                )}
              </div>
              {detail.error && (
                <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
                  {detail.error}
                </div>
              )}
              <div>
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Payload
                </p>
                <pre className="max-h-[60vh] overflow-auto rounded-md border bg-muted/50 p-3 text-xs">
                  {JSON.stringify(detail.payload, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function StatusBadge({ status }: { status: WebhookLog['status'] }) {
  if (status === 'success') {
    return (
      <Badge
        variant="outline"
        className="gap-1 border-emerald-500/40 text-emerald-600 dark:text-emerald-400"
      >
        <CheckCircle2Icon className="size-3" />
        Success
      </Badge>
    );
  }
  if (status === 'failed') {
    return (
      <Badge
        variant="outline"
        className="gap-1 border-amber-500/40 text-amber-700 dark:text-amber-400"
      >
        <AlertTriangleIcon className="size-3" />
        Failed
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="gap-1 border-destructive/40 text-destructive">
      <XCircleIcon className="size-3" />
      Error
    </Badge>
  );
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}
